import {
  Badge,
  Box,
  Flex,
  Heading,
  Image,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Switch,
  Text,
} from '@chakra-ui/react'
import type { Document, InsuranceParamsDto, OfferAddon } from 'BrokerTsLib/src/dtos'
import { OfferStatusEnum } from 'BrokerTsLib/src/enums/offerStatusEnum'
import moment from 'moment'
import { useContext, useEffect, useState } from 'react'

import { useAuth } from '@/context/AuthContext'

import { baseUrl } from '@/hooks'
import { MenuItem, MenuItemWithSubMenu, SubMenu } from '../../components/Menu/'
import { carkulkaExtraOffersContext } from '../../context/CarkulkaExtraOffersProvider'
import { useGetOfferDocuments } from '../../hooks/useDocuments'
import { useSetAddonSelectedVariant } from '../../hooks/useOfferAddon'
import { insuranceParamsContext } from '../../layouts/Wizard'
import { getLogo } from '../../utils/companiesLogos'

const documentUrl = `${baseUrl}/${import.meta.env.VITE_DOCUMENT_VEHICLE_API}`

const OfferHeaderButtons = ({ children }: { children: React.ReactNode }) => {
  return <Box sx={{ display: 'flex', gap: { base: 1, md: 2 } }}>{children}</Box>
}

const OfferHeaderButtonLabel = ({ children }: { children: React.ReactNode }) => {
  return <Text sx={{ display: { base: 'none', md: 'inline' } }}>{children}</Text>
}

interface OfferHeaderProps {
  offer: OfferAddon
  status: string
  color: string
}

export const OfferHeaderExtra = ({ offer, status, color }: OfferHeaderProps) => {
  const { calculatingOffersIds } = useContext(carkulkaExtraOffersContext)
  const isRecalculating = calculatingOffersIds?.includes(offer!.offerId) ?? false
  const { refetch: getOfferDocuments, data: offerDocuments } = useGetOfferDocuments(offer!.offerId, { enabled: false })
  const { insuranceParams: ip, refetchInsuranceParams } = useContext(insuranceParamsContext)
  const { mutateAsync: setAddonSelected } = useSetAddonSelectedVariant()
  const insuranceParams = ip as InsuranceParamsDto
  const { user } = useAuth()

  useEffect(() => {
    if (!isRecalculating && user?.enableOfferXml)
      setTimeout(() => {
        getOfferDocuments() // at nebrani nacteni jinym dulezitejsim requestum
      }, 5000)
  }, [isRecalculating, user?.enableOfferXml])

  const [selected, setSelected] = useState(insuranceParams.baseParams.addonVariants?.find((x) => x.riskVariantId == offer.riskVariantId)?.selected ?? false)

  const handleToggle = (selected: boolean) => {
    setSelected(selected)

    setAddonSelected({ activeOfferId: offer.offerId, selected: selected }).then(() =>
      refetchInsuranceParams(),
    )
  }

  return (
    <Box
      sx={{
        display: { base: 'grid', sm: 'flex' },
        alignContent: { base: 'stretch', sm: 'center' },
        justifyContent: { base: 'strecth', sm: 'space-between' },
        columnGap: 4,
        rowGap: 2,
        py: 2,
        px: 4,
        bgColor: color,
        borderBottom: color === 'white' ? '1px solid' : 'none',
        borderBottomColor: 'gray.200',
        borderTopRadius: 'md',
        minH: 16
      }}
      as="header"
    >
      <Flex
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          columnGap: { base: 4, lg: 8 },
        }}
      >
        <Flex sx={{ alignItems: 'center', columnGap: { base: 4, lg: 8 } }}>
          <Image
            src={getLogo(offer.insurerId)}
            alt={offer.insurerNameFull}
            width="64px"
            maxH="48px"
            style={{
              mixBlendMode: 'multiply',
              pointerEvents: 'none',
            }}
          />

          <Heading as="h2" sx={{ fontSize: { base: 'sm', lg: 'md' } }}>
            {offer.title ? offer.title : offer.insurerNameFull}
          </Heading>
        </Flex>

        {status === OfferStatusEnum.Error && (
          <Badge colorScheme="red">
            <Popover trigger="click">
              <PopoverTrigger>
                <Text cursor="pointer">Chyba kalkulace</Text>
              </PopoverTrigger>
              <PopoverContent w={'auto'} maxW={300}>
                <Stack direction={'column'}>
                  {offer?.errors.map((e, i) => {
                    return (
                      <Text key={i} variant="calcError" whiteSpace={'break-spaces'}>
                        {e.message}
                      </Text>
                    )
                  })}
                </Stack>
              </PopoverContent>
            </Popover>
          </Badge>
        )}

        {status === 'selected' && <Badge colorScheme="green">Vybraná nabídka</Badge>}

        {offer?.infos && offer.infos.length > 0 && (
          <Popover>
            <PopoverTrigger>
              <Badge sx={{ cursor: 'pointer' }} colorScheme="green" variant="solid">
                Info
              </Badge>
            </PopoverTrigger>

            <PopoverContent>
              <PopoverArrow />
              <PopoverBody sx={{ display: 'grid', gap: 2 }}>
                {
                  <Box
                    display="grid"
                    gap={2}
                    dangerouslySetInnerHTML={{
                      __html: offer!.infos.map((x) => x.message).join('<hr/>'),
                    }}
                  ></Box>
                }
              </PopoverBody>
            </PopoverContent>
          </Popover>
        )}
      </Flex>

      <Flex sx={{ alignItems: 'center', justifyContent: 'space-between', gap: { base: 2, lg: 8 } }}>

        <OfferHeaderButtons>
          {(offerDocuments?.data && offerDocuments.data.length > 0) &&
            <MenuItemWithSubMenu trigger='click'
              button={
                <MenuItem variant="outline" colorScheme='gray' color="gray.500">
                  <OfferHeaderButtonLabel>XML</OfferHeaderButtonLabel>
                </MenuItem>
              }
              subMenu={
                <SubMenu sx={{
                  maxHeight: '300px',
                  overflow: 'auto',
                  p: 2,
                  width: 'max-content',
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  bg: 'white',
                  borderRadius: 'md',
                  boxShadow: 'md',
                  listStyle: 'none',
                  zIndex: 'subMenu'
                }}
                >
                  <li>
                    {offerDocuments?.data.map((doc: Document) => {
                      return (
                        <MenuItem key={doc.id} target='_blank' href={`${documentUrl}/GetOfferDocument?docId=${doc.id}`}><>{doc.name} ({moment(doc.date).format('D.M.yyyy H:mm:ss')})</></MenuItem>
                      )
                    })}
                  </li>
                </SubMenu>
              }
            />
          }
        </OfferHeaderButtons>

        <Text sx={{ fontSize: 'sm' }}>Zahrnout do nabídky</Text>

        <Flex sx={{ gap: { base: 4, lg: 8 } }}>
          <Switch
            id={'offer-toggle'}
            defaultChecked={false}
            disabled={
              status === OfferStatusEnum.Error || !!insuranceParams.generalParams.contractNumber
            }
            isChecked={selected}
            onChange={(e) => handleToggle(e.target.checked)}
            size="md"
          />
        </Flex>
      </Flex>
    </Box>
  )
}
